import styled from 'styled-components';
import React, { useContext } from 'react';
import produce from 'immer';
import { useComputed, useSignal } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { TEMP_NO_SELECTOR } from '@/features/editor/widgets/changelog/placeholder';
import { LocationPicker } from '@/features/editor/widgets/shared/location-picker';
import { createNewElementMessageFromOrigChange } from '@/features/editor/context/use-transpiler';
import { EditorContext } from '@/features/editor/context/editor-context';
import { AllMutationKind, CompoundChange, HtmlMutationKind } from '@/pkg/sdk';
import { DeviceType } from '@/utils/definitions';
import {
  InspectorHeaderWrapper,
  InspectorTitleWrapper,
} from '@/features/editor/widgets/shared/inspector-widget-layout';
import { AiComponentTabContainer } from '@/features/editor/widgets/ai-component/tab-container';
import { AiComponentContextProvider } from '@/features/editor/widgets/ai-component/context';
import { BackEditorButton } from '@/features/editor/widgets/shared/inspector-widget-layout/back-editor-button';
import { VSpace } from '@/components/spacing';
import { TinyProgressBar } from '@/components/tiny-progress-bar';
import { PopConfirm } from '@/components/pop-confirm';

export interface AiComponentWidgetProps {
  originalChange: EditorDeclarativeBlock;
}

export function AiComponentWidget({ originalChange }: AiComponentWidgetProps) {
  useSignals();
  const {
    devicePreview: {
      editorState: { device },
    },
    inspectorNav: { gotoAppCatalog, gotoChangelog },
  } = useContext(EditorContext);

  const newChange = useSignal(originalChange);

  const onBackFromPicker = () => {
    const newChange = produce(originalChange, (draft) => {
      draft.block.selector = TEMP_NO_SELECTOR;
    });
    gotoAppCatalog(createNewElementMessageFromOrigChange(newChange));
  };

  const onSelectorPicked = (
    selector: string,
    kind: AllMutationKind,
    isInCart?: boolean,
  ) => {
    newChange.value = produce(newChange.value, (draft) => {
      draft.block.selector = selector;
      draft.editorSelector = selector;
      const block = draft?.block?.value as CompoundChange;
      if (block?.htmlKind) {
        block.htmlKind = kind as HtmlMutationKind;
      }
      draft.isInCart = isInCart;
    });
  };

  const props = useComputed(() => ({
    originalChange: newChange.value,
  }));

  const hasNoPlacement = useComputed(
    () => newChange?.value?.block?.selector === TEMP_NO_SELECTOR,
  );

  const hasChanges = useComputed(() => originalChange !== newChange.value);

  return (
    <>
      {hasNoPlacement.value && (
        <LocationPicker
          onContinue={onSelectorPicked}
          onBack={onBackFromPicker}
          title="Choose a location for the new section"
          backCaption="< Back to widgets"
        />
      )}
      {!hasNoPlacement.value && (
        <Wrapper device={device}>
          <VSpace value={4} />
          <InspectorHeaderWrapper>
            <span>
              {hasChanges ? (
                <PopConfirm
                  text="Are you sure you want to go back? Your current progress will be lost."
                  onConfirm={async () => gotoChangelog()}
                >
                  <BackEditorButton caption="< Back to experience" />
                </PopConfirm>
              ) : (
                <BackEditorButton
                  caption="< Back to experience"
                  onClick={gotoChangelog}
                />
              )}
            </span>
          </InspectorHeaderWrapper>
          <VSpace value={1} />
          <InspectorTitleWrapper>
            Create a custom block using Visually AI
          </InspectorTitleWrapper>
          <VSpace value={2} />
          <TinyProgressBar
            initialValue={80}
            heightInRem={0.6}
            widthInPercent={device === DeviceType.Desktop ? 60 : 35}
          />
          <VSpace value={2.5} />
          <AiComponentContextProvider
            key="ai-component-ctx-provider"
            props={props}
          >
            <AiComponentTabContainer />
          </AiComponentContextProvider>
          <VSpace value={4} />
        </Wrapper>
      )}
    </>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: ${(props: { device: DeviceType }) =>
    props.device === DeviceType.Desktop ? `100%` : `100%`};

  display: flex;
  flex-direction: column;
`;
